import { MomentInput } from 'moment';
import React from 'react';
import classNames from 'classnames';
import DateWithTimeSplitted from './date-with-time-splitted';
import { getIsSameDay, LayoutType } from './date-display';
import './formatted-time-window.scss';

export enum DateDisplayOption {
	// If 2 dates have same day, show only the first one day (concept example):
	// 1.1.20 15:00, 1.1.20 16:00 -> 1.1.20 15:00 - 16:00
	// 1.1.20 15:00, 2.1.20 16:00 -> 1.1.20 15:00 - 2.1.20 16:00
	SHOW_ONLY_FIRST_DATE_WHEN_SAME_DAY = 1,

	// Show date only if different day (concept example):
	// 1.1.20 15:00, 1.1.20 16:00 -> 15:00 - 16:00
	// 1.1.20 15:00, 2.1.20 16:00 -> 1.1.20 15:00 - 2.1.20 16:00
	SHOW_DATE_WHEN_DIFF_DAY
}

interface Props {
	firstDate: MomentInput | string;
	secondDate: MomentInput | string;
	hourFormat: string;
	timezone?: string;
	invalidDateText?: string;
	template?: LayoutType;
	dateDisplayType?: DateDisplayOption;
	dateFormat?: string;
}

function getShowDates(
	isSameDay: boolean,
	dateDisplayType: DateDisplayOption = DateDisplayOption.SHOW_ONLY_FIRST_DATE_WHEN_SAME_DAY
): { showFirstDate: boolean; showSecondDate: boolean } {
	if (dateDisplayType === DateDisplayOption.SHOW_DATE_WHEN_DIFF_DAY) {
		return {
			showFirstDate: !isSameDay,
			showSecondDate: !isSameDay
		};
	}

	if (dateDisplayType === DateDisplayOption.SHOW_ONLY_FIRST_DATE_WHEN_SAME_DAY) {
		return {
			showFirstDate: true,
			showSecondDate: !isSameDay
		};
	}
}

const FormattedTimeWindow = ({
	firstDate,
	secondDate,
	hourFormat,
	timezone,
	invalidDateText,
	template = LayoutType.VERTICAL,
	dateDisplayType = DateDisplayOption.SHOW_ONLY_FIRST_DATE_WHEN_SAME_DAY,
	dateFormat
}: Props) => {
	const isSameDay = getIsSameDay(firstDate, secondDate, timezone);
	const { showFirstDate, showSecondDate } = getShowDates(isSameDay, dateDisplayType);

	const getHyphenHtml = (
		<span className={classNames('formatted-time-window-hyphen', { 'same-date': isSameDay })}> - </span>
	);

	return (
		<div
			className={classNames('formatted-time-window', {
				horizontal: template === LayoutType.HORIZONTAL,
				inline: template === LayoutType.INLINE,
				'same-date': isSameDay
			})}
		>
			<div data-test-id="first-date" className="formatted-time-window-date">
				<DateWithTimeSplitted
					date={firstDate}
					hourFormat={hourFormat}
					timezone={timezone}
					template={template}
					invalidDateText={invalidDateText}
					showDate={showFirstDate}
					dateFormat={dateFormat}
				/>
				{template !== LayoutType.HORIZONTAL && getHyphenHtml}
			</div>
			{template === LayoutType.HORIZONTAL && getHyphenHtml}
			<div
				data-test-id="second-date"
				className={classNames('formatted-time-window-date', { 'same-date': isSameDay })}
			>
				<DateWithTimeSplitted
					showDate={showSecondDate}
					date={secondDate}
					hourFormat={hourFormat}
					timezone={timezone}
					template={template}
					dateFormat={dateFormat}
				/>
			</div>
		</div>
	);
};
export default FormattedTimeWindow;
